import axios from 'axios'
import { useSuspenseQuery } from '@tanstack/react-query'
import { z } from 'zod'

const NewsAuthorSchema = z.object({
  user_id: z.number(),
  name: z.string(),
})

export type NewsAuthor = z.infer<typeof NewsAuthorSchema>

const getNewsAuthors = async () => {
  const response = await axios.get('/api/news/authors')
  return z.array(NewsAuthorSchema).parse(response.data)
}

export const useNewsAuthors = () => {
  return useSuspenseQuery({
    queryKey: ['newsAuthors'],
    queryFn: getNewsAuthors,
  })
}

const MostReadNewsSchema = z.object({
  news_id: z.number(),
  title: z.string(),
})

export type MostReadNews = z.infer<typeof MostReadNewsSchema>

const getMostReadNews = async (count: number, competition_id?: number) => {
  const response = await axios.get('/api/news/most_read', {
    params: { count, competition_id },
  })
  return z.array(MostReadNewsSchema).parse(response.data)
}

export const useMostReadNews = (count: number, competition_id?: number) => {
  return useSuspenseQuery({
    queryKey: ['mostReadNews', { count, competition_id }],
    queryFn: () => getMostReadNews(count, competition_id),
  })
}

const getSingleNews = async ({ news_id }: { news_id: number }) => {
  const response = await axios.get(`/api/news/${news_id}`)
  return SingleNewsSchema.parse(response.data)
}

export const useSingleNews = ({ news_id }: { news_id: number }) => {
  return useSuspenseQuery({
    queryKey: ['singleNews', { news_id }],
    queryFn: () => getSingleNews({ news_id }),
  })
}

const getFeaturedNews = async ({
  competition_id,
}: {
  competition_id: number
}) => {
  const response = await axios.get('/api/news/featured', {
    params: { competition_id },
  })
  return FeaturedNewsSchema.nullable().parse(response.data)
}

export const useFeaturedNews = ({
  competition_id,
}: {
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['featuredNews', { competition_id }],
    queryFn: () => getFeaturedNews({ competition_id }),
  })
}

export type ApiNewsFilterType = {
  page: number
  page_size: number
  competition_id: number
  search?: string
  author?: number
  published_before?: string
  published_after?: string
  is_announcement?: boolean
}

const getAllNews = async (params: ApiNewsFilterType) => {
  const response = await axios.get('/api/news', { params })
  return NewsResponseSchema.parse(response.data)
}

export const useNews = (params: ApiNewsFilterType) => {
  return useSuspenseQuery({
    queryKey: ['allNews', params],
    queryFn: () => getAllNews(params),
  })
}

export const DocumentSchema = z.object({
  document: z.string(),
})

export const ReactionsSchema = z.object({
  created_at: z.string(),
  player_id: z.number().nullable(),
  reaction_id: z.number(),
  reaction_type: z.string(),
  user_id: z.number(),
})

export const ShortReactionsSchema = z.object({
  red_heart: z.number(),
  thumbs_up: z.number(),
  partying_face: z.number(),
  rocket: z.number(),
})

export const CommentsSchema = z.object({
  comment_id: z.number(),
  text: z.string(),
  user_id: z.number(),
  player_id: z.number().nullable(),
  reply_to_id: z.number().nullable(),
  reactions: z.array(ReactionsSchema),
})

export const BaseNewsSchema = z.object({
  title: z.string(),
  summary: z.string(),
  created_at: z.string(),
  competition_id: z.number().nullable(),
  external_link: z.string().nullable(),
  image: z.object({ main: z.string() }).partial(),
  image_background_color: z.string(),
  featured: z.boolean(),
})

export const SingleNewsSchema = BaseNewsSchema.extend({
  body: z.string(),
  document: z.record(z.string()).nullable().optional(),
  comments: z.array(CommentsSchema),
  reactions: z.array(ReactionsSchema),
})

export const NewsSchema = BaseNewsSchema.extend({
  news_id: z.number().optional(),
  comment_count: z.number().optional(),
  documents: DocumentSchema.partial().optional(),
  reactions: ShortReactionsSchema.partial().optional(),
})

export const NewsResponseSchema = z.object({
  news: z.array(NewsSchema),
  total: z.number(),
})

export const FeaturedNewsSchema = BaseNewsSchema.extend({
  news_id: z.number().optional(),
  reactions: ShortReactionsSchema.partial().optional(),
})

export type News = z.infer<typeof NewsSchema>
export type FeaturedNews = z.infer<typeof FeaturedNewsSchema>
